/**
 * Store-scoped route shell.
 *
 * WHY the connection is resolved here and handed down through the outlet: every child screen
 * needs the store's id and its default currency, and resolving it once means a URL pointing at
 * a store this seller no longer owns fails in one place with a real message instead of six
 * different 404s.
 */
import type { ReactNode } from "react";
import { Link, Outlet, useOutletContext, useParams } from "react-router-dom";
import type { Connection } from "../api";
import { Shell } from "../components/Shell";
import { EmptyState } from "../components/States";
import { useConnections } from "../stores";

export function StoreLayout(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { connections } = useConnections();
  const connection = connections.find((c) => c.id === id);

  if (!connection) {
    return (
      <Shell connectionId={null}>
        <EmptyState
          title="Store not found"
          hint="This store is not connected to your account, or it was disconnected."
          action={<Link className="btn" to="/">Back to your stores</Link>}
        />
      </Shell>
    );
  }

  return (
    <Shell connectionId={connection.id}>
      <Outlet context={connection} />
    </Shell>
  );
}

export function useStore(): Connection {
  return useOutletContext<Connection>();
}
